<template>
    <div :id="getTableId()" class="relative DocumentsTable">
        <div v-show="loading" class="absolute z-50 w-full h-full bg-spinner flex flex-col items-center">
            <spinner class="mb-1"></spinner>
        </div>

        <div class="border-t-2 border-blue-600">
            <div class="xs:flex-col table-header">
                <div class="relative w-full sm:w-auto">
                    <label class="self-center text-black mr-1" for="perPage">Per Page:</label>
                    <select class="form-control form-control-sm form-control-fixed-sm" id="perPage" name="perPage" v-model.number="perPage">
                        <option value="5">5</option>
                        <option value="15">15</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option :value="total">All</option>
                    </select>
                </div>
                <div class="relative w-full md:w-auto md:my-0 my-2 ml-auto">
                    <input class="form-input-inline form-input-sm-inline -mr-6" style="width:auto !important;" @keyup.enter="setFilter" placeholder="Enter a keyword..." v-model="searchFor" aria-label="Enter a keyword" />
                    <button class="inline-block search-clear" @click="resetFilter" aria-label="Reset"><i class="fas fa-times"/></button>
                    <button class="inline-block btn btn-sm btn-yellow" @click="setFilter" aria-label="Search"><i class="fas fa-search"/></button>
                </div>
            </div>
        </div>

        <div class="table-responsive">
            <vuetable
                ref="vuetableRef"
                :css="{
                    tableClass: 'table table-bordered table-hover',
                    tableBodyClass: '',
                    loadingClass: 'Please wait...',
                    ascendingIcon: 'fas fa-sort-alpha-down',
                    descendingIcon: 'fas fa-sort-alpha-up',
                    handleIcon: 'fas fa-bars',
                    sortableIcon: 'fas fa-sort text-gray-500',
                    ascendingClass: 'sorted-asc',
                    descendingClass: 'sorted-desc',
                }"
                :api-url="apiUrl"
                :fields="fields"
                :sort-order="sortOrder"
                :per-page="perPage"
                track-by="id"
                :append-params="moreParams"
                pagination-path=""
                @vuetable:pagination-data="onPaginationData"
                @vuetable:loading="onLoading"
                @vuetable:loaded="onLoaded"
                @vuetable:load-success="updateTotal"
            >
                <template #published-slot="props">
                    {{ toDateFormat(props.rowData.published) }}
                </template>
                <template #profit="props">
                    <div :class="{'bg-blue-100': props.rowData.profit === 'Yes', 'bg-red-100': props.rowData.profit === 'No'}">
                        {{ props.rowData.profit }}
                    </div>
                </template>
                <template #title-slot="props">
                    <a href="#" @click.prevent="onActionClicked(titleAction || 'preview', props.rowData)">
                        {{ props.rowData.title }}
                    </a>
                </template>
                <template #actions="props">
                    <div class="text-center">
                        <button @click="onActionClicked('download', props.rowData)" class="text-blue-600" title="Download"><i class="fas fa-download px-1"></i></button>
                        <button v-if="externalUri" @click="onActionClicked('external-open', props.rowData)" class="text-blue-600" title="View in new window"><i class="fas fa-external-link-alt px-1"></i></button>
                    </div>
                </template>
            </vuetable>
            <div class="mt-4 text-center">
                <vuetable-pagination
                    :css="{
                        icons: {
                            first: 'fas fa-angle-double-left',
                            prev: 'fas fa-angle-left',
                            next: 'fas fa-angle-right',
                            last: 'fas fa-angle-double-right'
                        }
                    }"
                    ref="paginationRef"
                    @vuetable-pagination:change-page="onChangePage"
                ></vuetable-pagination>

                <vuetable-pagination-info
                    class="mt-2"
                    ref="paginationInfoRef"
                    info-class="pagination-info"
                ></vuetable-pagination-info>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';

const props = defineProps({
    url: {
        type: String,
        default: '/documents/type/newsletter',
    },
    externalUri: {
        type: String,
        default: '',
    },
    dateFormat: {
        type: String,
        default: 'numeric',
    },
    includeProfitColumn: {
        type: Boolean,
        default: false,
    },
    titleAction: {
        type: String,
        default: 'preview',
    },
    fields: {
        type: Array,
        default: () => [
            {
                name: 'published-slot',
                title: 'Published',
                sortField: 'published',
                width: "15%",
                titleClass: "text-left",
                dataClass: "text-left",
            },
            {
                name: 'title-slot',
                title: 'Title',
                sortField: 'title',
                width: "32%",
                titleClass: "text-center",
                dataClass: "",
            },
            {
                name: 'description',
                title: 'Description',
                sortField: 'description',
                width: "45%",
                titleClass: "text-center",
                dataClass: "",
            },
            {
                name: 'actions',
                title: 'Actions',
                width: "7%",
                titleClass: "text-center",
                dataClass: "binary/options",
            },
        ],
    },
    fromDate: {
        type: String,
        default: '',
    },
});

const vuetableRef = ref(null);
const paginationRef = ref(null);
const paginationInfoRef = ref(null);
const apiUrl = props.url;
const searchFor = ref('');
const loading = ref(true);
const perPage = ref(25);
const total = ref(25);
const limitDate = ref(props.fromDate);

const sortOrder = ref([
    {
        field: 'created_at',
        direction: 'desc',
    },
]);

const moreParams = ref({
    from_date: limitDate.value,
});

const getTableId = () => 'DocumentsTable-' + new Date().getTime();

const toDateFormat = (value) => {
    if (props.dateFormat === 'string') {
        let date = new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        return date;
    }
    return value;
};

const setFilter = async () => {
    moreParams.value = {
        filter: searchFor.value,
        from_date: limitDate.value,
    };
    await nextTick();
    vuetableRef.value.refresh();
};

const resetFilter = () => {
    searchFor.value = '';
    setFilter();
};

const onLoading = () => {
    loading.value = true;
};

const onLoaded = () => {
    loading.value = false;
};

const onPaginationData = (tablePagination) => {
    paginationRef.value.setPaginationData(tablePagination);
    paginationInfoRef.value.setPaginationData(tablePagination);
};

const onChangePage = (page) => {
    vuetableRef.value.changePage(page);
};

const onActionClicked = (action, data) => {
    if (action === 'external-open') {
        window.open(props.externalUri + data.url, '_blank');
    } else if (action === 'preview') {
        window.open('/preview' + data.url);
    } else {
        window.open('/download' + data.url);
    }
};

const updateTotal = (response) => {
    total.value = response.data.total;
};

const refreshTable = () => {
    vuetableRef.value.refresh();
};

onMounted(() => {
    refreshTable();
});
</script>

<style lang="scss">
.DocumentsTable {
    .bg-spinner {
        background: -moz-linear-gradient(top, rgba(242, 221, 161 ,0.4) 0%, rgba(0,0,0,0) 100%) !important;
        background: -webkit-linear-gradient(top, rgba(242, 221, 161 ,0.4) 0%,rgba(0,0,0,0) 100%) !important;
        background: linear-gradient(to bottom, rgba(242, 221, 161 ,0.4) 0%,rgba(0,0,0,0) 100%) !important;
    }

    .spinner {
        .spinner-child {
            background-color: #d38653 !important;
        }
    }

    .vuetable {
        th {
            padding-top: .4rem !important;
            padding-bottom: .4rem !important;
            i {
                @apply leading-normal;
            }
        }

        th, td {
            padding-left: .6rem !important;
            padding-right: .6rem !important;
        }
    }

    .pagination {
        a {
            cursor: pointer;
            @apply px-2 py-1 ml-1 text-blue-600;

            &.active {
                @apply text-white bg-blue-600 rounded;
            }

            &:hover {
                @apply text-white bg-blue-400 rounded;
            }

            &.btn-nav.disabled {
                cursor: not-allowed;
                @apply text-gray-400;
            }
        }
    }
    .vuetable-pagination-info {
        @apply text-sm;
    }
}
</style>
